import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Refresh } from '@mui/icons-material';
import { Rows3, Search, Trash2, WrapText } from 'lucide-react';
import { useState } from 'react';

type SalePinActionsProps = {
  pinnedCount: number;
  pinWrapMode?: boolean;
  onPinWrapModeChange?: (wrap: boolean) => void;
  onRefresh?: () => void | Promise<void>;
  onOpenSearch?: () => void;
  onUnpinAll?: () => void | Promise<void>;
};

const baseButtonClass = 'h-8 gap-1.5 px-2.5 text-xs font-medium shadow-none';

export function SalePinActions({
  pinnedCount,
  pinWrapMode = true,
  onPinWrapModeChange,
  onRefresh,
  onOpenSearch,
  onUnpinAll,
}: SalePinActionsProps) {
  const [showUnpinAllDialog, setShowUnpinAllDialog] = useState(false);
  const [isClearingPins, setIsClearingPins] = useState(false);
  const [isRefreshingPins, setIsRefreshingPins] = useState(false);

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshingPins) return;
    setIsRefreshingPins(true);
    try {
      await onRefresh();
      toast({
        title: 'Refreshed',
        description: 'Pinned variants refreshed successfully.',
      });
    } catch {
      toast({
        title: 'Refresh failed',
        description: 'Unable to refresh pinned variants. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshingPins(false);
    }
  };

  const handleClearAllPins = async () => {
    if (!onUnpinAll || isClearingPins) return;
    setIsClearingPins(true);
    try {
      await onUnpinAll();
      setShowUnpinAllDialog(false);
    } finally {
      setIsClearingPins(false);
    }
  };

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className={cn(baseButtonClass, 'text-emerald-700 border-emerald-200 hover:bg-emerald-50')}
          onClick={() => void handleRefresh()}
          disabled={isRefreshingPins}
        >
          <Refresh className={cn('h-3.5 w-3.5', isRefreshingPins && 'animate-spin')} />
          {isRefreshingPins ? 'Refreshing…' : 'Refresh Pin'}
        </Button>

        {onOpenSearch && (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenSearch}
            className={cn(baseButtonClass, 'text-blue-700 border-blue-200 hover:bg-blue-50')}
          >
            <Search className="h-3.5 w-3.5" />
            Search
          </Button>
        )}

        {onPinWrapModeChange && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPinWrapModeChange(!pinWrapMode)}
            className={cn(
              baseButtonClass,
              pinWrapMode
                ? 'text-violet-700 border-violet-200 hover:bg-violet-50'
                : 'text-slate-600 border-slate-200 hover:bg-slate-50',
            )}
          >
            {pinWrapMode ? (
              <>
                <Rows3 className="h-3.5 w-3.5" />
                Scroll Pin
              </>
            ) : (
              <>
                <WrapText className="h-3.5 w-3.5" />
                Wrap Pin
              </>
            )}
          </Button>
        )}

        {pinnedCount > 0 && onUnpinAll && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowUnpinAllDialog(true)}
            className={cn(baseButtonClass, 'text-red-600 border-red-200 hover:bg-red-50')}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Clear Pin
          </Button>
        )}
      </div>

      <AlertDialog
        open={showUnpinAllDialog}
        onOpenChange={(open) => {
          if (!isClearingPins) setShowUnpinAllDialog(open);
        }}
      >
        <AlertDialogContent className="max-w-[90vw] md:max-w-lg rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Pinned Variants?</AlertDialogTitle>
            <AlertDialogDescription>
              This will unpin all {pinnedCount} pinned variant
              {pinnedCount !== 1 ? 's' : ''}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col md:flex-row gap-2">
            <AlertDialogCancel className="w-full md:w-auto mt-0" disabled={isClearingPins}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleClearAllPins();
              }}
              disabled={isClearingPins}
              className="bg-red-600 hover:bg-red-700 w-full md:w-auto"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {isClearingPins ? 'Clearing…' : 'Clear All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
